import styled from '@emotion/styled';
import { FC, useEffect, useState } from 'react';
import {
  DownloadStatus,
  ICourse,
  TrainingCourseProgressInfo,
} from '@taleemabad/db';
import { CourseList } from '../../../molecules/course-list/course-list';
import {
  ProgressInformationProps,
  Spacing,
  IconText,
  Typography,
  StyledDiv,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Capacitor } from '@capacitor/core';
import { ChipFilterWithContainer } from '../../../atoms/chip/chip-filter';

interface TeacherTrainingCoursesProps {
  allCourses: ICourse[];
  isDownloadEnabled?: boolean;
  progressInformation: ProgressInformationProps;
  onCourseClick: (course: ICourse) => void;
  onTrainingCourseProgressBannerClick: (
    course: TrainingCourseProgressInfo,
  ) => void;
  loadingComponent?: () => any;
  onDownload?: (course: ICourse) => void;
  onDownloadDelete?: (course: ICourse) => void;
}

const courseType = [
  {
    id: 1,
    name: 'All',
  },
];
export const TeacherTrainingCourses: FC<TeacherTrainingCoursesProps> = ({
  allCourses,
  isDownloadEnabled = false,
  onCourseClick,
  loadingComponent,
  onDownload,
  onDownloadDelete,
}) => {
  const [selectecCourse, setSelectCourses] = useState(1);
  const [selectedCourseList, setSelectedCourseList] =
    useState<ICourse[]>(allCourses);
  useEffect(() => {
    if (allCourses.length > 0) {
      const trainingList =
        selectecCourse === 2
          ? [...allCourses]
              .filter(
                (course) => course?.downloadStatus === DownloadStatus.Completed,
              )
              .sort((a, b) =>
                a?.downloadStatus === DownloadStatus.Completed &&
                b?.downloadStatus !== DownloadStatus.Completed
                  ? -1
                  : b?.downloadStatus === DownloadStatus.Completed &&
                      a?.downloadStatus !== DownloadStatus.Completed
                    ? 1
                    : 0,
              )
          : allCourses;
      trainingList.sort((a, b) => a.index - b.index);
      setSelectedCourseList(trainingList);
    }
  }, [selectecCourse, allCourses]);
  return (
    <TeacherTrainingCourseContainer>
      <Spacing marginTop="xxl"></Spacing>
      <Spacing marginTop="xxl">
        <StickyContainer>
          <HeadlineContainer>
            <IconText
              icon="import_contacts"
              text="Courses"
              size="large"
              textVariant="textTitleLargeNeutral10"
              iconColor="--md-ref-neutral-10"
              data-cy="teacher-training-courses-headline"
            ></IconText>
          </HeadlineContainer>
        </StickyContainer>
        {Capacitor.isNativePlatform() && isDownloadEnabled && (
          <StyledDiv gap={10} marginTop={16} marginBottom={16}>
            {courseType.map((item) => (
              <ChipFilterWithContainer
                selected={selectecCourse === item.id}
                onClick={() => {
                  setSelectCourses(item.id);
                }}
                key={item.name}
                label={item.name}
              />
            ))}
          </StyledDiv>
        )}
        <CourseList
          isDownloadEnabled={isDownloadEnabled}
          courses={selectedCourseList}
          onCourseClick={onCourseClick}
          onDownload={(data) => {
            onDownload?.(data);
          }}
          onDownloadDelete={(data) => {
            onDownloadDelete?.(data);
          }}
        />
        {loadingComponent?.()}
      </Spacing>
      {selectedCourseList.length === 0 &&
        selectecCourse === 2 &&
        isDownloadEnabled && (
          <StyledNoDownloadLp>
            <Typography variant="textTitleMediumNeutral10">
              Your downloaded courses <br />
              will appear here
            </Typography>
          </StyledNoDownloadLp>
        )}
    </TeacherTrainingCourseContainer>
  );
};

const TeacherTrainingCourseContainer = styled.div`
  padding: var(--rk-spacing-xl);
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--md-sys-color-on-primary);
`;

const HeadlineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
  }
`;

const StyledNoDownloadLp = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 350px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 475px);
  }
`;
